<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Button as ShadcnButton } from 'dashboard/components-shadcn/new-york/ui/button';
import { Input } from 'dashboard/components-shadcn/new-york/ui/input';
import { Badge } from 'dashboard/components-shadcn/new-york/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'dashboard/components-shadcn/new-york/ui/popover';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from 'dashboard/components-shadcn/new-york/ui/command';

const props = defineProps({
  searchQuery: {
    type: String,
    default: '',
  },
  selectedStatuses: {
    type: Array,
    default: () => [],
  },
  selectedCategories: {
    type: Array,
    default: () => [],
  },
  selectedInboxIds: {
    type: Array,
    default: () => [],
  },
  statusOptions: {
    type: Array,
    default: () => [],
  },
  categoryOptions: {
    type: Array,
    default: () => [],
  },
  inboxOptions: {
    type: Array,
    default: () => [],
  },
  isStatusOpen: {
    type: Boolean,
    default: false,
  },
  isCategoryOpen: {
    type: Boolean,
    default: false,
  },
  isInboxOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'update:searchQuery',
  'update:selectedStatuses',
  'update:selectedCategories',
  'update:selectedInboxIds',
  'update:isStatusOpen',
  'update:isCategoryOpen',
  'update:isInboxOpen',
]);

const { t } = useI18n();

const hasActiveFilters = computed(
  () =>
    props.searchQuery ||
    props.selectedStatuses.length > 0 ||
    props.selectedCategories.length > 0 ||
    props.selectedInboxIds.length > 0
);

const toggleStatus = id => {
  const next = [...props.selectedStatuses];
  const idx = next.indexOf(id);
  if (idx === -1) next.push(id);
  else next.splice(idx, 1);
  emit('update:selectedStatuses', next);
};

const toggleCategory = id => {
  const next = [...props.selectedCategories];
  const idx = next.indexOf(id);
  if (idx === -1) next.push(id);
  else next.splice(idx, 1);
  emit('update:selectedCategories', next);
};

const toggleInbox = id => {
  const next = [...props.selectedInboxIds];
  const idx = next.indexOf(id);
  if (idx === -1) next.push(id);
  else next.splice(idx, 1);
  emit('update:selectedInboxIds', next);
};

const clearAllFilters = () => {
  emit('update:searchQuery', '');
  emit('update:selectedStatuses', []);
  emit('update:selectedCategories', []);
  emit('update:selectedInboxIds', []);
};
</script>

<template>
  <div class="space-y-2">
    <!-- Toolbar row -->
    <div
      class="flex flex-wrap items-center gap-3 p-3 bg-n-alpha-1 dark:bg-n-alpha-2 rounded-lg border border-n-weak"
    >
      <!-- Search input -->
      <div class="relative flex-1 min-w-[200px] max-w-sm">
        <span
          class="i-lucide-search absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-n-slate-10"
        />
        <Input
          :model-value="searchQuery"
          :placeholder="t('CAMPAIGN.WHATSAPP_OFFICIAL.SEARCH_PLACEHOLDER')"
          class="!pl-9 h-9"
          @input="emit('update:searchQuery', $event.target.value)"
        />
      </div>

      <!-- Status filter popover -->
      <Popover
        :open="isStatusOpen"
        @update:open="emit('update:isStatusOpen', $event)"
      >
        <PopoverTrigger as-child>
          <ShadcnButton
            variant="outline"
            size="sm"
            class="h-9 border-dashed border-n-weak bg-transparent hover:bg-n-alpha-2 dark:bg-n-solid-3 dark:hover:bg-n-alpha-3"
          >
            <span class="i-lucide-circle-dot w-4 h-4 mr-2 text-n-slate-11" />
            <span class="text-n-slate-11">{{
              t('WHATSAPP_TEMPLATES.TABLE_HEADER.STATUS')
            }}</span>
            <template v-if="selectedStatuses.length > 0">
              <span class="mx-2 h-4 w-px bg-n-slate-6" />
              <Badge
                variant="secondary"
                class="rounded-sm px-1 font-normal bg-n-slate-3 dark:bg-n-slate-5 text-n-slate-11"
              >
                {{ selectedStatuses.length }}
              </Badge>
            </template>
          </ShadcnButton>
        </PopoverTrigger>
        <PopoverContent
          class="w-[200px] p-0 bg-white dark:bg-n-solid-3 border-n-weak"
          align="start"
        >
          <Command class="bg-transparent">
            <CommandList>
              <CommandGroup>
                <CommandItem
                  v-for="opt in statusOptions"
                  :key="opt.id"
                  :value="opt.id"
                  class="flex items-center gap-2 cursor-pointer text-n-slate-11 hover:bg-n-alpha-2 dark:hover:bg-n-alpha-3"
                  @select="toggleStatus(opt.id)"
                >
                  <div
                    class="flex h-4 w-4 items-center justify-center rounded-sm border border-n-slate-6"
                    :class="
                      selectedStatuses.includes(opt.id)
                        ? 'bg-n-brand border-n-brand-ink text-white'
                        : ''
                    "
                  >
                    <span
                      v-if="selectedStatuses.includes(opt.id)"
                      class="i-lucide-check h-3 w-3"
                    />
                  </div>
                  <!-- eslint-disable-next-line vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
                  <span>{{ opt.icon }}</span>
                  <span>{{
                    t(`WHATSAPP_TEMPLATES.STATUS.${opt.id}`) || opt.label
                  }}</span>
                </CommandItem>
              </CommandGroup>
            </CommandList>
            <div
              v-if="selectedStatuses.length > 0"
              class="border-t border-n-weak p-1"
            >
              <ShadcnButton
                variant="ghost"
                size="sm"
                class="w-full justify-center text-xs h-8 text-n-slate-11 hover:text-n-ruby-11 hover:bg-n-ruby-3 dark:hover:bg-n-ruby-4"
                @click="
                  emit('update:selectedStatuses', []);
                  emit('update:isStatusOpen', false);
                "
              >
                {{ t('CAMPAIGN.WHATSAPP_OFFICIAL.FILTERS.CLEAR') }}
              </ShadcnButton>
            </div>
          </Command>
        </PopoverContent>
      </Popover>

      <!-- Category filter popover -->
      <Popover
        :open="isCategoryOpen"
        @update:open="emit('update:isCategoryOpen', $event)"
      >
        <PopoverTrigger as-child>
          <ShadcnButton
            variant="outline"
            size="sm"
            class="h-9 border-dashed border-n-weak bg-transparent hover:bg-n-alpha-2 dark:bg-n-solid-3 dark:hover:bg-n-alpha-3"
          >
            <span class="i-lucide-tag w-4 h-4 mr-2 text-n-slate-11" />
            <span class="text-n-slate-11">{{
              t('WHATSAPP_TEMPLATES.TABLE_HEADER.CATEGORY')
            }}</span>
            <template v-if="selectedCategories.length > 0">
              <span class="mx-2 h-4 w-px bg-n-slate-6" />
              <Badge
                variant="secondary"
                class="rounded-sm px-1 font-normal bg-n-slate-3 dark:bg-n-slate-5 text-n-slate-11"
              >
                {{ selectedCategories.length }}
              </Badge>
            </template>
          </ShadcnButton>
        </PopoverTrigger>
        <PopoverContent
          class="w-[220px] p-0 bg-white dark:bg-n-solid-3 border-n-weak"
          align="start"
        >
          <Command class="bg-transparent">
            <CommandList>
              <CommandGroup>
                <CommandItem
                  v-for="opt in categoryOptions"
                  :key="opt.id"
                  :value="opt.id"
                  class="flex items-center gap-2 cursor-pointer text-n-slate-11 hover:bg-n-alpha-2 dark:hover:bg-n-alpha-3"
                  @select="toggleCategory(opt.id)"
                >
                  <div
                    class="flex h-4 w-4 items-center justify-center rounded-sm border border-n-slate-6"
                    :class="
                      selectedCategories.includes(opt.id)
                        ? 'bg-n-brand border-n-brand-ink text-white'
                        : ''
                    "
                  >
                    <span
                      v-if="selectedCategories.includes(opt.id)"
                      class="i-lucide-check h-3 w-3"
                    />
                  </div>
                  <!-- eslint-disable-next-line vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
                  <span>{{ opt.icon }}</span>
                  <span>{{
                    t(`WHATSAPP_TEMPLATES.CATEGORY.${opt.id}`) || opt.label
                  }}</span>
                </CommandItem>
              </CommandGroup>
            </CommandList>
            <div
              v-if="selectedCategories.length > 0"
              class="border-t border-n-weak p-1"
            >
              <ShadcnButton
                variant="ghost"
                size="sm"
                class="w-full justify-center text-xs h-8 text-n-slate-11 hover:text-n-ruby-11 hover:bg-n-ruby-3 dark:hover:bg-n-ruby-4"
                @click="
                  emit('update:selectedCategories', []);
                  emit('update:isCategoryOpen', false);
                "
              >
                {{ t('CAMPAIGN.WHATSAPP_OFFICIAL.FILTERS.CLEAR') }}
              </ShadcnButton>
            </div>
          </Command>
        </PopoverContent>
      </Popover>

      <!-- Inbox filter popover -->
      <Popover
        :open="isInboxOpen"
        @update:open="emit('update:isInboxOpen', $event)"
      >
        <PopoverTrigger as-child>
          <ShadcnButton
            variant="outline"
            size="sm"
            class="h-9 border-dashed border-n-weak bg-transparent hover:bg-n-alpha-2 dark:bg-n-solid-3 dark:hover:bg-n-alpha-3"
          >
            <span class="i-lucide-inbox w-4 h-4 mr-2 text-n-slate-11" />
            <span class="text-n-slate-11">{{
              t('CAMPAIGN.WHATSAPP_OFFICIAL.INBOX')
            }}</span>
            <template v-if="selectedInboxIds.length > 0">
              <span class="mx-2 h-4 w-px bg-n-slate-6" />
              <Badge
                variant="secondary"
                class="rounded-sm px-1 font-normal bg-n-slate-3 dark:bg-n-slate-5 text-n-slate-11"
              >
                {{ selectedInboxIds.length }}
              </Badge>
            </template>
          </ShadcnButton>
        </PopoverTrigger>
        <PopoverContent
          class="w-[240px] p-0 bg-white dark:bg-n-solid-3 border-n-weak"
          align="start"
        >
          <Command class="bg-transparent">
            <CommandList>
              <CommandGroup>
                <CommandItem
                  v-for="opt in inboxOptions"
                  :key="opt.id"
                  :value="opt.id"
                  class="flex items-center gap-2 cursor-pointer text-n-slate-11 hover:bg-n-alpha-2 dark:hover:bg-n-alpha-3"
                  @select="toggleInbox(opt.id)"
                >
                  <div
                    class="flex h-4 w-4 items-center justify-center rounded-sm border border-n-slate-6"
                    :class="
                      selectedInboxIds.includes(opt.id)
                        ? 'bg-n-brand border-n-brand-ink text-white'
                        : ''
                    "
                  >
                    <span
                      v-if="selectedInboxIds.includes(opt.id)"
                      class="i-lucide-check h-3 w-3"
                    />
                  </div>
                  <span
                    class="i-lucide-message-square w-3.5 h-3.5 text-n-teal-11"
                  />
                  <span class="truncate">{{ opt.label }}</span>
                </CommandItem>
              </CommandGroup>
            </CommandList>
            <div
              v-if="selectedInboxIds.length > 0"
              class="border-t border-n-weak p-1"
            >
              <ShadcnButton
                variant="ghost"
                size="sm"
                class="w-full justify-center text-xs h-8 text-n-slate-11 hover:text-n-ruby-11 hover:bg-n-ruby-3 dark:hover:bg-n-ruby-4"
                @click="
                  emit('update:selectedInboxIds', []);
                  emit('update:isInboxOpen', false);
                "
              >
                {{ t('CAMPAIGN.WHATSAPP_OFFICIAL.FILTERS.CLEAR') }}
              </ShadcnButton>
            </div>
          </Command>
        </PopoverContent>
      </Popover>

      <!-- Clear all button -->
      <ShadcnButton
        v-if="hasActiveFilters"
        variant="ghost"
        size="sm"
        class="h-9 text-n-slate-11 hover:text-n-slate-12"
        @click="clearAllFilters"
      >
        <span class="i-lucide-x w-4 h-4 mr-1" />
        {{ t('CAMPAIGN.WHATSAPP_OFFICIAL.FILTERS.CLEAR') }}
      </ShadcnButton>
    </div>

    <!-- Active filter pills -->
    <div v-if="hasActiveFilters" class="flex flex-wrap items-center gap-2">
      <span class="text-xs text-n-slate-10">
        {{ t('CAMPAIGN.WHATSAPP_OFFICIAL.FILTERS.ACTIVE') }}:
      </span>

      <div
        v-if="searchQuery"
        class="inline-flex items-center gap-1.5 px-2 py-1 bg-n-brand/10 text-n-brand-ink text-xs rounded-full border border-n-brand/30"
      >
        <span class="i-lucide-search w-3 h-3" />
        <!-- eslint-disable-next-line vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
        <span>"{{ searchQuery }}"</span>
        <button
          class="hover:bg-n-brand/20 rounded-full p-0.5 transition-colors"
          @click="emit('update:searchQuery', '')"
        >
          <span class="i-lucide-x w-3 h-3" />
        </button>
      </div>

      <div
        v-for="st in selectedStatuses"
        :key="st"
        class="inline-flex items-center gap-1.5 px-2 py-1 bg-n-teal-3 text-n-teal-11 text-xs rounded-full border border-n-teal-6"
      >
        <!-- eslint-disable-next-line vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
        <span>{{ statusOptions.find(o => o.id === st)?.icon }}</span>
        <span>{{ t(`WHATSAPP_TEMPLATES.STATUS.${st}`) || st }}</span>
        <button
          class="hover:bg-n-teal-4 rounded-full p-0.5 transition-colors"
          @click="toggleStatus(st)"
        >
          <span class="i-lucide-x w-3 h-3" />
        </button>
      </div>

      <div
        v-for="cat in selectedCategories"
        :key="cat"
        class="inline-flex items-center gap-1.5 px-2 py-1 bg-n-blue-3 text-n-blue-11 text-xs rounded-full border border-n-blue-6"
      >
        <!-- eslint-disable-next-line vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
        <span>{{ categoryOptions.find(o => o.id === cat)?.icon }}</span>
        <span>{{ t(`WHATSAPP_TEMPLATES.CATEGORY.${cat}`) || cat }}</span>
        <button
          class="hover:bg-n-blue-4 rounded-full p-0.5 transition-colors"
          @click="toggleCategory(cat)"
        >
          <span class="i-lucide-x w-3 h-3" />
        </button>
      </div>
      <div
        v-for="inboxId in selectedInboxIds"
        :key="'inbox-' + inboxId"
        class="inline-flex items-center gap-1.5 px-2 py-1 bg-n-teal-3 text-n-teal-11 text-xs rounded-full border border-n-teal-6"
      >
        <span class="i-lucide-inbox w-3 h-3" />
        <span>{{
          inboxOptions.find(o => o.id === inboxId)?.label || inboxId
        }}</span>
        <button
          class="hover:bg-n-teal-4 rounded-full p-0.5 transition-colors"
          @click="toggleInbox(inboxId)"
        >
          <span class="i-lucide-x w-3 h-3" />
        </button>
      </div>
    </div>
  </div>
</template>
